import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MobileCardView } from "@/components/ui/mobile-card-view";
import { useIsMobile } from "@/hooks/use-mobile";
import { ClientBilling } from "@/types/billing";
import {
  MoreHorizontal,
  FileText,
  Settings,
  ExternalLink,
  Link,
  Unlink,
  Building2,
  ChevronRight,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ClientBillingTableProps {
  clients: ClientBilling[];
  onGenerateInvoice?: (client: ClientBilling) => void;
  onConfigureMarkup?: (client: ClientBilling) => void;
  isGeneratingInvoice?: boolean;
  /** Tenant IDs that have custom markup (non-inherited) */
  customMarkupTenantIds?: string[];
  /** Default markup percentage for inheritance display */
  defaultMarkup?: number;
  /** Whether data is loading */
  isLoading?: boolean;
}

/** Format currency for display */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

/** Get badge variant based on status */
const getStatusBadge = (status: ClientBilling["status"]) => {
  switch (status) {
    case "current":
      return <Badge className="bg-green-100 text-green-800">Current</Badge>;
    case "overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "pending":
      return <Badge variant="secondary">Pending</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

/** Loading skeleton for table rows */
function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
          <TableCell className="text-right"><Skeleton className="h-4 w-24 ml-auto" /></TableCell>
          <TableCell className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableCell>
          <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
          <TableCell><Skeleton className="h-5 w-16" /></TableCell>
          <TableCell><Skeleton className="h-8 w-8" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

/** Loading skeleton for mobile cards */
function MobileCardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-56" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-24" />
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Skeleton className="h-9 flex-1" />
              <Skeleton className="h-9 flex-1" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ClientBillingTable({
  clients,
  onGenerateInvoice,
  onConfigureMarkup,
  isGeneratingInvoice,
  customMarkupTenantIds = [],
  defaultMarkup,
  isLoading = false,
}: ClientBillingTableProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  /** Check if a client has custom (non-inherited) markup */
  const hasCustomMarkup = (tenantId: string) => customMarkupTenantIds.includes(tenantId);

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => b.total_billed - a.total_billed);
  }, [clients]);

  const handleViewDetails = (client: ClientBilling) => {
    navigate(`/agency/billing/clients/${client.tenant_id}`);
  };

  const renderMobileCard = (client: ClientBilling) => (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium truncate">{client.tenant_name}</h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            Base: {formatCurrency(client.base_cost)} + {client.markup_percentage}% markup
            {hasCustomMarkup(client.tenant_id) ? (
              <Badge variant="outline" className="ml-1 text-xs px-1 py-0">Custom</Badge>
            ) : (
              <Badge variant="secondary" className="ml-1 text-xs px-1 py-0">Inherited</Badge>
            )}
          </p>
        </div>
        {getStatusBadge(client.status)}
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Total Billed</span>
          <p className="font-medium">{formatCurrency(client.total_billed)}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Unbilled</span>
          <p className="font-medium">{formatCurrency(client.unbilled_amount)}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => handleViewDetails(client)}
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Details
        </Button>
        {onGenerateInvoice && client.unbilled_amount > 0 && (
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={() => onGenerateInvoice(client)}
            disabled={isGeneratingInvoice}
          >
            <FileText className="h-4 w-4 mr-1" />
            Invoice
          </Button>
        )}
      </div>
    </div>
  );

  // Handle loading state
  if (isLoading) {
    if (isMobile) {
      return <MobileCardSkeleton count={3} />;
    }
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead className="text-right">Base Cost</TableHead>
              <TableHead className="text-right">Markup</TableHead>
              <TableHead className="text-right">Total Billed</TableHead>
              <TableHead className="text-right">Unbilled</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableSkeleton rows={5} />
          </TableBody>
        </Table>
      </div>
    );
  }

  // Handle empty state
  if (clients.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground font-medium">No client billing data available</p>
          <p className="text-sm text-muted-foreground mt-1">
            Client usage will appear here once they start using services.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isMobile) {
    return (
      <MobileCardView
        data={sortedClients.map((c) => ({ ...c, id: c.tenant_id }))}
        renderCard={renderMobileCard}
      />
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Client</TableHead>
            <TableHead className="text-right">Base Cost</TableHead>
            <TableHead className="text-right">Markup</TableHead>
            <TableHead className="text-right">Total Billed</TableHead>
            <TableHead className="text-right">Unbilled</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedClients.map((client) => (
            <TableRow
              key={client.tenant_id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => handleViewDetails(client)}
            >
              <TableCell className="font-medium">{client.tenant_name}</TableCell>
              <TableCell className="text-right">
                {formatCurrency(client.base_cost)}
              </TableCell>
              <TableCell className="text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-medium">{client.markup_percentage}%</span>
                        {hasCustomMarkup(client.tenant_id) ? (
                          <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-blue-50 text-blue-700 border-blue-200">
                            <Unlink className="h-3 w-3 mr-1" />
                            Custom
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5">
                            <Link className="h-3 w-3 mr-1" />
                            Inherited
                          </Badge>
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {hasCustomMarkup(client.tenant_id) ? (
                        <div className="space-y-1">
                          <p className="font-medium text-blue-600">Custom Markup</p>
                          <p className="text-xs text-muted-foreground">
                            {client.tenant_name} has a custom {client.markup_percentage}% markup that overrides the agency default.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="font-medium text-green-600">Inherited Settings</p>
                          <div className="flex items-center gap-1 text-xs">
                            <Building2 className="h-3 w-3" />
                            <span>Agency Default</span>
                            <ChevronRight className="h-3 w-3" />
                            <span className="font-medium">{defaultMarkup ?? client.markup_percentage}%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {client.tenant_name} inherits markup from the agency default settings.
                          </p>
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="ml-2 text-green-600 text-sm">
                  +{formatCurrency(client.markup_amount)}
                </span>
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(client.total_billed)}
              </TableCell>
              <TableCell className="text-right">
                {client.unbilled_amount > 0 ? (
                  <span className="text-amber-600 font-medium">
                    {formatCurrency(client.unbilled_amount)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>{getStatusBadge(client.status)}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleViewDetails(client)}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    {onConfigureMarkup && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onConfigureMarkup(client);
                        }}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Configure Markup
                      </DropdownMenuItem>
                    )}
                    {onGenerateInvoice && client.unbilled_amount > 0 && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onGenerateInvoice(client);
                        }}
                        disabled={isGeneratingInvoice}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Generate Invoice
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
